/**
 * /try UI Controller (DOM orchestration only).
 *
 * WHAT THIS FILE DOES:
 * - Wires the /try DOM surface to WebTryClient API calls.
 * - Creates inline result cards for submit progress and final share links.
 * - Resolves share URLs with same-origin WS handoff for ready-state anchors.
 * - Manages textarea auto-resize, drag-and-drop, and file preview.
 * - Handles language selection and submit flow.
 * - Drives auth modal interactions and header status label.
 *
 * WHY THIS EXISTS:
 * - Keeps DOM code isolated from auth + API logic.
 * - Prevents the entrypoint from becoming a god module.
 * - Makes UI behavior easier to reason about and modify.
 *
 * CALLED BY:
 * - src/client/try.ts (entry point) with a WebTryClient instance.
 *
 * THIS FILE CALLS:
 * - WebTryClient for ingest, auth, and share creation.
 * - try-input-widgets.ts for DOM listener wiring (extracted verbatim).
 * - try-submit-flow.ts for result-card/pipeline/share helpers (extracted verbatim).
 * - resolveTryErrorMessage() for user-friendly errors.
 *
 * STATE NOTES:
 * - selectedLanguage tracks the active language dropdown choice.
 * - currentFile holds the attached file (if any).
 * - isSubmitting guards against duplicate submissions.
 * - activeResultCardId points at the single in-flight inline result card.
 * - speculationController tracks speculative ingest lifecycle and input locks.
 * - Most members are intentionally public (not `private`) because the
 *   extracted helpers call back through the instance and unit tests stub
 *   members directly.
 *
 * ERROR HANDLING:
 * - All thrown WebTryError instances are mapped to UI messages.
 * - UI-only errors are handled locally without impacting client state.
 *
 * DESIGN PRINCIPLES:
 * - Keep streaming logic minimal and isolated:
 *   - WS parsing/connection lives in try-ws.ts
 *   - Share navigation + WS handoff lives in try-share-navigation.ts
 * - Keep DOM references explicit and documented.
 *
 * NON-GOALS:
 * - API orchestration (handled by try-client.ts).
 * - Auth token management (handled by try-auth.ts).
 *
 * @file src/client/try-ui.ts
 */

import type {
  IngestResponse,
  TryFileUploadProgress,
  TryMediaIngestPhaseEvent
} from './try-shared.js';
import {
  resolveTryErrorMessage,
  WebTryError
} from './try-shared.js';
import type { WebTryClient } from './try-client.js';
import { buildTryMediaTelemetryEvents, TryMediaTelemetrySources } from './try-media-telemetry.js';
import { track as trackWebEvent } from './web-telemetry.js';
import {
  ShareNavigationModes,
  type ShareNavigationMode
} from './try-share-navigation.js';
import { TrySpeculationController } from './try-speculation.js';
import { markError, markReady } from './try-result-card.js';
import { isDoclingDocumentFile } from '../lib/try-document-file.js';
import { applyTryCreatePresetDom } from './try-create-preset-dom.js';
import type { TryMode } from './try-mode-tabs.js';
import { GistPipelineStatusLabels } from '@gist-protocol/core/shared/gist-pipeline-status.js';
import * as widgets from './try-input-widgets.js';
import * as flow from './try-submit-flow.js';
import type { TrySubmitNavigateMode } from './try-submit-flow.js';

declare global {
  interface Window {
    /** Injected by `src/ssr/try.ts` (`buildTryPublicJsonValue`). */
    __GIST_TRY_PUBLIC__?: { origin: string; joinWithTrySourceUrl: string };
  }
}

/**
 * Fallback subscribe URL when a 402 body omits `checkoutUrl` (staging-safe).
 */
function resolveTrySubscribeFallbackUrl(): string {
  const pub = window.__GIST_TRY_PUBLIC__;
  if (
    typeof pub?.joinWithTrySourceUrl === 'string' &&
    /^https?:\/\//.test(pub.joinWithTrySourceUrl)
  ) {
    return pub.joinWithTrySourceUrl;
  }
  return `${window.location.origin}/join?source=try`;
}

/**
 * UI Controller for the /try page.
 *
 * Handles:
 * - Textarea auto-resize and keyboard shortcuts
 * - File upload via button click and drag-and-drop
 * - File preview with remove functionality
 * - Language selector dropdown
 * - Form submission and status display
 *
 * DOM DEPENDENCIES (defined in ssr/try.ts):
 * - #try-input-container: Input container (for drag states)
 * - #try-textarea: Main content input
 * - #try-file-input: Hidden file input
 * - #try-upload-btn: Triggers file picker
 * - #try-file-preview: File preview container
 * - #try-file-name: File name display
 * - #try-file-remove: Remove file button
 * - #try-lang-selector: Language dropdown container
 * - #try-lang-trigger: Globe button
 * - #try-lang-label: Selected language label
 * - #try-lang-dropdown: Language options list
 * - #try-submit-btn: Submit/CTA button
 * - #try-helper: Inline helper text (speculation status)
 * - #try-status: Status message display
 * - #try-toast: Brief inline toast for input lock feedback
 * - #try-signin-btn: Sign in button
 */
export class TryUIController {
  currentFile: File | null = null;
  currentFileDurationMs: number | null = null;
  selectedLanguage = 'en';
  readonly defaultLanguage = 'en';
  isSubmitting = false;
  activeResultCardId: string | null = null;

  /**
   * A-041: Cached scrollHeight to avoid redundant style recalcs.
   * Only updated when the textarea's scrollHeight actually changes.
   */
  lastScrollHeight = 0;

  /**
   * Timer ID for the pipeline cycle ticker during Gist generation.
   * Cleared when submit completes or errors.
   */
  pipelineCycleTimerId: ReturnType<typeof setInterval> | null = null;

  /**
   * Speculation controller for URL/file input guards.
   */
  speculationController: TrySpeculationController | null = null;

  /**
   * Tracks auth fingerprint to reset speculation on sign-in/out.
   */
  lastAuthKey: string | null = null;

  // DOM Elements
  inputContainer: HTMLDivElement | null = null;
  textarea: HTMLTextAreaElement | null = null;
  fileInput: HTMLInputElement | null = null;
  uploadBtn: HTMLButtonElement | null = null;
  filePreview: HTMLDivElement | null = null;
  fileIconHost: HTMLElement | null = null;
  fileName: HTMLSpanElement | null = null;
  fileRemove: HTMLButtonElement | null = null;
  langSelector: HTMLDivElement | null = null;
  langTrigger: HTMLButtonElement | null = null;
  langLabel: HTMLSpanElement | null = null;
  langDropdown: HTMLDivElement | null = null;
  submitBtn: HTMLButtonElement | null = null;
  helperText: HTMLParagraphElement | null = null;
  /**
   * Optional create-module wrapper rendered when the tabs flag is on. Acts
   * as the drop scope so dragging anywhere inside the wrapper auto-switches
   * to File mode, and as a CSS hook for per-preset affordance styling.
   */
  createModule: HTMLDivElement | null = null;
  /** Optional inline hint shown under the input in tabs mode (Phase 2). */
  modeHint: HTMLParagraphElement | null = null;
  /**
   * Optional callback registered by `try.ts` to switch the active create-module
   * tab. The drop handler invokes this to force File mode when a file is
   * dropped from any preset.
   */
  modeChanger: ((mode: TryMode) => void) | null = null;
  status: HTMLDivElement | null = null;
  toast: HTMLDivElement | null = null;
  signinBtn: HTMLButtonElement | null = null;
  authOverlay: HTMLDivElement | null = null;
  authModal: HTMLDivElement | null = null;
  authClose: HTMLButtonElement | null = null;
  authForm: HTMLFormElement | null = null;
  authEmail: HTMLInputElement | null = null;
  authPassword: HTMLInputElement | null = null;
  authSubmit: HTMLButtonElement | null = null;
  authGoogle: HTMLButtonElement | null = null;
  authError: HTMLDivElement | null = null;
  isSignedIn = false;
  /** A-030: Element that had focus before the auth modal opened, restored on close. */
  previouslyFocusedElement: HTMLElement | null = null;
  /** A-032: Counter-based drag tracking to prevent flicker on child element boundaries. */
  dragOverCounter = 0;
  /** A-035: Timer ID for auto-clearing success status messages. */
  successStatusTimerId: ReturnType<typeof setTimeout> | null = null;

  constructor(readonly client: WebTryClient) {}

  /**
   * Initializes the UI controller when DOM is ready.
   */
  init(): void {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this.mount());
    } else {
      this.mount();
    }
  }

  /** Guards against double-mount: mount() registers document-level listeners with no teardown path. */
  private mounted = false;

  /**
   * Mounts event listeners on DOM elements.
   */
  private mount(): void {
    if (this.mounted) return;
    this.mounted = true;
    this.inputContainer = document.getElementById('try-input-container') as HTMLDivElement;
    this.textarea = document.getElementById('try-textarea') as HTMLTextAreaElement;
    this.fileInput = document.getElementById('try-file-input') as HTMLInputElement;
    this.uploadBtn = document.getElementById('try-upload-btn') as HTMLButtonElement;
    this.filePreview = document.getElementById('try-file-preview') as HTMLDivElement;
    this.fileIconHost = document.getElementById('try-file-icon-host');
    this.fileName = document.getElementById('try-file-name') as HTMLSpanElement;
    this.fileRemove = document.getElementById('try-file-remove') as HTMLButtonElement;
    this.langSelector = document.getElementById('try-lang-selector') as HTMLDivElement;
    this.langTrigger = document.getElementById('try-lang-trigger') as HTMLButtonElement;
    this.langLabel = document.getElementById('try-lang-label') as HTMLSpanElement;
    this.langDropdown = document.getElementById('try-lang-dropdown') as HTMLDivElement;
    this.submitBtn = document.getElementById('try-submit-btn') as HTMLButtonElement;
    this.helperText = document.getElementById('try-helper') as HTMLParagraphElement;
    this.createModule = document.getElementById('try-create-module') as HTMLDivElement | null;
    this.modeHint = document.getElementById('try-mode-hint') as HTMLParagraphElement | null;
    this.status = document.getElementById('try-status') as HTMLDivElement;
    this.toast = document.getElementById('try-toast') as HTMLDivElement;
    this.signinBtn = document.getElementById('try-signin-btn') as HTMLButtonElement;
    this.authOverlay = document.getElementById('try-auth-overlay') as HTMLDivElement;
    this.authModal = document.getElementById('try-auth-modal') as HTMLDivElement;
    this.authClose = document.getElementById('try-auth-close') as HTMLButtonElement;
    this.authForm = document.getElementById('try-auth-form') as HTMLFormElement;
    this.authEmail = document.getElementById('try-auth-email') as HTMLInputElement;
    this.authPassword = document.getElementById('try-auth-password') as HTMLInputElement;
    this.authSubmit = document.getElementById('try-auth-submit') as HTMLButtonElement;
    this.authGoogle = document.getElementById('try-auth-google') as HTMLButtonElement;
    this.authError = document.getElementById('try-auth-error') as HTMLDivElement;

    if (!this.textarea || !this.submitBtn) {
      console.warn('[try-ui] Required DOM elements not found');
      return;
    }

    // A-036: Enable submit button now that JS has hydrated.
    // The SSR template renders it with `disabled` to prevent clicks before JS loads.
    this.submitBtn.disabled = false;

    widgets.setupTextarea(this);
    this.setupFileUpload();
    widgets.setupLanguageSelector(this);
    widgets.setupSubmit(this);
    widgets.setupSignIn(this);
    widgets.setupAuthStateListener(this);
    this.initializeSpeculation();
    // A-004: Reset submit state when page is restored from bfcache.
    // /try now opens the share in a new tab and usually stays active, but users
    // can still navigate away and return via Back. If the page is restored from
    // bfcache with a stale submitting state, clear it immediately.
    window.addEventListener('pageshow', (e) => {
      if (e.persisted) {
        this.isSubmitting = false;
        this.activeResultCardId = null;
        this.setSubmitLoading(false);
        this.showStatus('', 'hidden');
      }
    });
  }

  /** Wires file upload + drag-and-drop (extracted to try-input-widgets.ts). */
  setupFileUpload(): void {
    widgets.setupFileUpload(this);
  }

  // ── Speculative Ingest ────────────────────────────────────────────

  /**
   * Captures helper text defaults and syncs the initial speculation UI.
   */
  private initializeSpeculation(): void {
    this.speculationController = new TrySpeculationController({
      dom: {
        helperText: this.helperText,
        uploadBtn: this.uploadBtn,
        fileInput: this.fileInput,
        textarea: this.textarea,
        toast: this.toast
      },
      client: this.client,
      getGistLanguage: () => this.selectedLanguage
    });
    this.speculationController.initialize();
  }

  /** Delegates to try-input-widgets.ts (kept as a method so tests can stub it). */
  handleTextareaInput(): void {
    widgets.handleTextareaInput(this);
  }

  /** Delegates to try-input-widgets.ts (kept as a method so tests can stub it). */
  async handleFileAttachmentAttempt(file: File): Promise<void> {
    await widgets.handleFileAttachmentAttempt(this, file);
  }

  // ── Submit Flow ─────────────────────────────────────────────────────

  /**
   * Handles form submission.
   *
   * Flow:
   * 1. Guard duplicate submissions; require content or file.
   * 2. Create the inline result card synchronously.
   * 3. ASYNC: Ingest content → get contentId.
   * 4. ASYNC: Resolve share URL (inline publicUrl or createShare fallback).
   * 5. Mark the card ready and reset /try input for the next submit.
   */
  async handleSubmit(): Promise<void> {
    if (this.isSubmitting) return;

    const content = this.textarea?.value.trim() ?? '';
    const file = this.currentFile;
    const submitStartedAtMs = this.readNowMs();
    let submitMetricMode: TrySubmitNavigateMode | null = null;

    if (!content && !file) {
      // A-017: Nudge the user so an empty submit doesn't look broken.
      // Uses 'success' so the 4s auto-dismiss in showStatus() clears it once
      // the user starts typing — this is a hint, not a user error.
      this.showStatus('Paste a URL, drop a file, or type something to get started.', 'success');
      this.textarea?.focus();
      return;
    }

    const resultCardId = flow.createSubmitResultCard(this, content, file);

    this.isSubmitting = true;
    this.setSubmitLoading(true);
    flow.showPipelineStatus(this, 'processing', GistPipelineStatusLabels.LOADING);

    try {
      const response = await this.ingestContent(content, file);

      if (!response?.contentId) {
        throw new WebTryError('content_id_missing');
      }
      submitMetricMode = this.resolveSubmitNavigateMode({ content, file });
      const { shareUrl, usedInlineShare } = await this.resolveSubmitShareUrl(
        response,
        ShareNavigationModes.NEW_TAB
      );

      this.emitSubmitToNavigateMetric({
        submitStartedAtMs,
        mode: submitMetricMode,
        usedInlineShare
      });
      markReady(resultCardId, shareUrl);
      this.activeResultCardId = null;
      this.resetSubmitUiAfterSuccess();
      this.showStatus('', 'hidden');
      return;

    } catch (error) {
      flow.clearPipelineCycleCycler(this);
      const message = resolveTryErrorMessage(error);
      markError(resultCardId, message);
      this.activeResultCardId = null;

      // 402 / payment_required: surface a subscribe link (same path as /join).
      // Server-side web-ingest paid gating that reliably returns 402 for unpaid
      // users is Phase 4; until then this path is rarely hit E2E.
      if (error instanceof WebTryError && error.message === 'payment_required') {
        const checkoutUrl =
          (error.details as { checkoutUrl?: string } | undefined)?.checkoutUrl
          ?? resolveTrySubscribeFallbackUrl();
        widgets.showSubscribeStatus(this, message, checkoutUrl);
      } else {
        this.showStatus('', 'hidden');
      }

      this.isSubmitting = false;
      this.setSubmitLoading(false);
    }
  }

  /**
   * Routes content to the correct ingest method.
   *
   * Extracted from handleSubmit to keep the orchestration logic clean.
   *
   * @param content - Trimmed textarea value.
   * @param file - Attached file (if any).
   * @returns IngestResponse with contentId.
   */
  async ingestContent(content: string, file: File | null): Promise<IngestResponse> {
    if (file) {
      const speculativeUpload = await this.speculationController?.resolveSpeculativeFileUpload(file) ?? null;
      if (speculativeUpload) {
        if (speculativeUpload.kind === 'pdf') {
          return await this.client.ingestDocumentSubmitOnly({
            uploadId: speculativeUpload.uploadId,
            fileName: speculativeUpload.fileName,
            gistLanguage: this.selectedLanguage,
            createShare: true
          });
        }

        return await this.client.ingestMediaSubmitOnly({
          uploadId: speculativeUpload.uploadId,
          fileName: speculativeUpload.fileName,
          gistLanguage: this.selectedLanguage,
          createShare: true
        });
      }

      if (isDoclingDocumentFile(file)) {
        flow.showPipelineStatus(this, 'processing', GistPipelineStatusLabels.LOADING);
        return await this.client.ingestDocument({
          file,
          gistLanguage: this.selectedLanguage,
          createShare: true,
          onUploadProgress: (progress) => this.showUploadProgressStatus(progress)
        });
      }
      return await this.client.ingestMedia({
        file,
        gistLanguage: this.selectedLanguage,
        createShare: true,
        durationMs: this.currentFileDurationMs ?? undefined,
        onPhaseChange: (event) => this.handleMediaIngestPhaseChange(event),
        onTranscodeDecision: (telemetry) => {
          const events = buildTryMediaTelemetryEvents({
            source: TryMediaTelemetrySources.UI_SUBMIT,
            telemetry
          });
          const authState = this.client.getAuthStateLabel();
          for (const event of events) {
            trackWebEvent(event.event, { ...(event.metadata ?? {}), authState });
          }
        },
        onUploadProgress: (progress) => this.showUploadProgressStatus(progress)
      });
    }

    const url = flow.extractUrl(this, content);
    if (url) {
      const speculative = await this.speculationController?.resolveSpeculativeUrlResponse(content) ?? null;
      if (speculative) {
        return await this.ensureShareForSpeculativeUrlResponse(speculative);
      }

      return await this.client.ingestUrl({
        url,
        gistLanguage: this.selectedLanguage,
        createShare: true
      });
    }

    return await this.client.ingestText({ text: content, gistLanguage: this.selectedLanguage, createShare: true });
  }

  /** Delegates to try-submit-flow.ts (kept as a method so tests can stub it). */
  async resolveSubmitShareUrl(
    response: IngestResponse,
    navigationMode: ShareNavigationMode
  ): Promise<{ shareUrl: string; usedInlineShare: boolean }> {
    return await flow.resolveSubmitShareUrl(this, response, navigationMode);
  }

  /**
   * Resets /try so the form is ready for immediate next submit.
   */
  private resetSubmitUiAfterSuccess(): void {
    flow.clearPipelineCycleCycler(this);
    this.isSubmitting = false;
    this.activeResultCardId = null;
    this.setSubmitLoading(false);

    if (this.currentFile) {
      widgets.removeFile(this);
    }
    this.currentFile = null;
    this.currentFileDurationMs = null;
    if (this.fileInput) {
      this.fileInput.value = '';
    }

    if (this.textarea) {
      this.textarea.value = '';
      this.textarea.style.height = 'auto';
      this.lastScrollHeight = 0;
    }

    this.speculationController?.handleTextareaInput('', false);
  }

  /** Delegates to try-input-widgets.ts (kept as a method so tests can stub it). */
  updateTextareaHeight(): void {
    widgets.updateTextareaHeight(this);
  }

  /** Delegates to try-submit-flow.ts (kept as a method so tests can stub it). */
  async ensureShareForSpeculativeUrlResponse(response: IngestResponse): Promise<IngestResponse> {
    return await flow.ensureShareForSpeculativeUrlResponse(this, response);
  }

  /** Delegates to try-submit-flow.ts (kept as a method so tests can stub it). */
  emitSubmitToNavigateMetric(params: {
    submitStartedAtMs: number;
    mode: TrySubmitNavigateMode | null;
    usedInlineShare: boolean;
  }): void {
    flow.emitSubmitToNavigateMetric(this, params);
  }

  /** Delegates to try-submit-flow.ts (kept as a method so tests can stub it). */
  resolveSubmitNavigateMode(params: {
    content: string;
    file: File | null;
  }): TrySubmitNavigateMode | null {
    return flow.resolveSubmitNavigateMode(this, params);
  }

  /**
   * Reads a monotonic timestamp for local client timing measurements.
   */
  readNowMs(): number {
    if (typeof performance !== 'undefined' && typeof performance.now === 'function') {
      return performance.now();
    }
    return Date.now();
  }

  // ── Utilities ───────────────────────────────────────────────────────

  /** Delegates to try-input-widgets.ts (kept as a method so tests can stub it). */
  showUploadProgressStatus(progress: TryFileUploadProgress): void {
    widgets.showUploadProgressStatus(this, progress);
  }

  /** Delegates to try-submit-flow.ts (kept as a method so tests can stub it). */
  handleMediaIngestPhaseChange(event: TryMediaIngestPhaseEvent): void {
    flow.handleMediaIngestPhaseChange(this, event);
  }

  /** Delegates to try-input-widgets.ts (kept as a method so tests can stub it). */
  setSubmitLoading(loading: boolean): void {
    widgets.setSubmitLoading(this, loading);
  }

  /** Delegates to try-input-widgets.ts (kept as a method so tests can stub it). */
  showStatus(message: string, type: 'loading' | 'success' | 'error' | 'hidden'): void {
    widgets.showStatus(this, message, type);
  }

  // ── Create-module preset API (Phase 2) ──────────────────────────────

  /**
   * Applies a preset's placeholder, helper, hint, data attributes, and
   * (optionally) focus target to the existing /try DOM. Called by the tab
   * bar's `onChange` callback in `try.ts` and by drop-induced mode switches.
   *
   * Tabs are educational presets only — this method never gates submit or
   * routes through a different ingest path.
   *
   * @param mode - active create-module preset.
   * @param opts.focus - when true, focuses the preset's `focusTarget`.
   *   The entry passes `false` on initial mount and `true` on user-initiated
   *   activations so the page does not steal focus on first paint.
   */
  applyCreatePreset(mode: TryMode, opts: { focus: boolean } = { focus: false }): void {
    const applied = applyTryCreatePresetDom(
      {
        textarea: this.textarea,
        helperText: this.helperText,
        modeHint: this.modeHint,
        createModule: this.createModule,
        inputContainer: this.inputContainer,
        uploadBtn: this.uploadBtn
      },
      mode,
      opts
    );
    // Keep the speculation controller's idle-state copy in sync so its next
    // updateSpeculationUi() does not restore the SSR default helper.
    this.speculationController?.setHelperDefaultText(applied.helper);
  }

  /**
   * Registers a callback that lets the drop handler force a tab change
   * (e.g. switching to File mode when a file is dropped from any preset).
   *
   * The entry file (`src/client/try.ts`) wires this once, immediately after
   * mounting `mountTryModeTabs(...)`. Calling more than once replaces the
   * previous callback; the controller does not own the tabs handle directly.
   */
  registerCreateModeChanger(fn: (mode: TryMode) => void): void {
    this.modeChanger = fn;
  }
}
